// 004 (FR-027–FR-037, FR-053; US3; BR-1): add an address (/addresses/new) or edit one
// (/addresses/<id>). Edit starts placed at the saved pin and sends only changed properties;
// latitude and longitude always together; a cleared landmark is sent as null. Add sends an
// Idempotency-Key (kept for retries of the same payload).
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { View } from "react-native";

import { type AppError, isAppError } from "@/api/errors";
import type { Api } from "@/api/types";
import { newIdempotencyKey } from "@/lib/idempotencyKey";
import type { PinValue } from "@/maps/PinPicker";
import { appNotices } from "@/shell/notice/store";
import { StateView } from "@/shell/StateView";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";

import { PROFILE_COPY, profileErrorMessage } from "../copy";
import { useProfileClient } from "../profileApi";
import { Card, OfflineNote, ProfileFrame, useProfileStyles } from "../ui";
import { useParentData } from "../useParentData";
import { type AddressErrors, validateAddress } from "../validation";
import { canAddAddress } from "../viewModels";
import { AddressForm } from "./AddressForm";
import {
  type AddressFields,
  createAddressBody,
  draftFromAddress,
  emptyDraft,
  updateAddressBody,
} from "./addressBody";

type Load =
  | { s: "loading" }
  | { s: "ready"; address: Api["Address"] | null }
  | { s: "notFound" }
  | { s: "error"; error: AppError };

function Editor({ original }: { original: Api["Address"] | null }) {
  const styles = useProfileStyles();
  const router = useRouter();
  const { online } = useNetworkStatus();
  const { addresses, actions } = useParentData();
  const [draft, setDraft] = useState<AddressFields>(() =>
    original ? draftFromAddress(original) : emptyDraft(),
  );
  const savedPin = original
    ? { latitude: original.latitude, longitude: original.longitude }
    : undefined;
  const [pin, setPin] = useState<PinValue>({
    placed: original !== null,
    center: savedPin ?? null,
  });
  const [errors, setErrors] = useState<AddressErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [key, setKey] = useState<{ key: string; body: string } | null>(null);
  const back = () =>
    router.canGoBack() ? router.back() : router.replace("/profile");

  if (
    original === null &&
    addresses.data &&
    !canAddAddress(addresses.data.length)
  ) {
    return (
      <Card testID="address-limit">
        <Text>{PROFILE_COPY.addressLimit}</Text>
        <Button variant="outline" onPress={back}>
          {PROFILE_COPY.backToProfile}
        </Button>
      </Card>
    );
  }

  async function save() {
    if (pending || !online) return;
    const next = validateAddress({ ...draft, pinPlaced: pin.placed });
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0 || !pin.center) return;
    setPending(true);
    try {
      if (original === null) {
        const body = createAddressBody(draft, pin.center);
        const json = JSON.stringify(body);
        const k = key && key.body === json ? key.key : newIdempotencyKey();
        setKey({ key: k, body: json });
        await actions.addAddress(body, k);
      } else {
        const body = updateAddressBody(draft, pin.center, original);
        if (Object.keys(body).length > 0)
          await actions.updateAddress(original.id, body);
      }
      appNotices.post(PROFILE_COPY.addressSaved);
      back();
    } catch (e) {
      if (!isAppError(e)) throw e;
      const msg = profileErrorMessage(e, { action: "address" });
      setErrors((msg.fields ?? {}) as AddressErrors);
      setFormError(msg.form ?? null);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card testID="address-editor">
      <AddressForm
        value={draft}
        onChange={setDraft}
        errors={errors}
        savedPin={savedPin}
        onPin={setPin}
      />
      {formError ? (
        <InlineMessage tone="error">{formError}</InlineMessage>
      ) : null}
      <OfflineNote online={online} />
      <View style={styles.buttons}>
        <Button variant="outline" onPress={back}>
          {PROFILE_COPY.cancel}
        </Button>
        <Button
          onPress={() => void save()}
          pending={pending}
          disabled={!online || !pin.placed}
        >
          {PROFILE_COPY.save}
        </Button>
      </View>
    </Card>
  );
}

export function AddressFormScreen({ mode }: { mode: "create" | "edit" }) {
  const params = useLocalSearchParams<{ addressId?: string }>();
  const addressId =
    typeof params.addressId === "string" ? params.addressId : "";
  const router = useRouter();
  const client = useProfileClient();
  const { addresses, actions } = useParentData();
  const cached =
    mode === "edit"
      ? (addresses.data?.find((a) => a.id === addressId) ?? null)
      : null;
  const [load, setLoad] = useState<Load>(() =>
    mode === "create"
      ? { s: "ready", address: null }
      : cached
        ? { s: "ready", address: cached }
        : { s: "loading" },
  );

  // Create needs the list for the limit; edit opened directly loads the address.
  const listMissing = addresses.data === null;
  useEffect(() => {
    if (mode === "create" && listMissing) void actions.refresh();
  }, [mode, listMissing, actions]);

  const needsLoad = load.s === "loading";
  useEffect(() => {
    if (!needsLoad) return;
    let live = true;
    client.getAddress(addressId).then(
      (address) => {
        if (live) setLoad({ s: "ready", address });
      },
      (e: unknown) => {
        if (!live || !isAppError(e)) return;
        setLoad(
          e.kind === "notFound" ? { s: "notFound" } : { s: "error", error: e },
        );
      },
    );
    return () => {
      live = false;
    };
  }, [needsLoad, client, addressId]);

  let body;
  if (load.s === "ready") body = <Editor original={load.address} />;
  else if (load.s === "notFound")
    body = (
      <Card testID="address-not-found">
        <Text>{PROFILE_COPY.notFoundAddress}</Text>
        <Button variant="outline" onPress={() => router.replace("/profile")}>
          {PROFILE_COPY.backToProfile}
        </Button>
      </Card>
    );
  else if (load.s === "error")
    body = (
      <StateView
        status="error"
        error={load.error}
        onRetry={() => setLoad({ s: "loading" })}
      />
    );
  else body = <StateView status="loading" />;

  return (
    <ProfileFrame testID="address-form-screen" narrow>
      <Text variant="display" accessibilityRole="header" aria-level={1}>
        {mode === "create"
          ? PROFILE_COPY.newAddressTitle
          : PROFILE_COPY.editAddressTitle}
      </Text>
      {body}
    </ProfileFrame>
  );
}
